import { useWorkOrderCategoriesList } from "api/workOrders";
import CustomReactSelect from "components/CustomReactSelect";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const WorkOrderCategorySelector = ({
    value,
    onChange,
    label = "Spare Part Tag",
    placeholder = "Select Tag...",
    isMulti = false,
    required = false,
    disabled = false,
    creatable = false,
    ...props
}) => {
    const queryClient = useQueryClient();
    const workOrderCategoryQuery = useWorkOrderCategoriesList();

    const workOrderCategories = useMemo(
        () => (workOrderCategoryQuery.data ?? []).map(item => ({
            label: item,
            value: item,
        })),
        [workOrderCategoryQuery.data]
    );

    const handleFetch = useCallback(async (inputValue = "") => {
        const normalizedInput = inputValue.trim().toLowerCase();
        if (!normalizedInput) {
            return workOrderCategories;
        }

        return workOrderCategories.filter(option => option.label.toLowerCase().includes(normalizedInput));
    }, [workOrderCategories]);

    const getSelectedValue = () => {
        if (!value) return null;
        if (isMulti) {
            return workOrderCategories.filter(option => value.includes(option.value)) || null;
        }
        return workOrderCategories.find(option => option.value === value) || null;
    };

    const handleChange = (selectedOption) => {
        if (isMulti) {
            const selectedValues = selectedOption ? selectedOption.map(opt => opt.value) : [];
            if (onChange) {
                onChange(selectedValues, selectedOption);
            }
        } else {
            const selectedValue = selectedOption ? selectedOption.value : null;
            if (onChange) {
                onChange(selectedValue, selectedOption);
            }
        }
    };

    const handleCreate = (inputValue) => {
        const newOption = {
            label: inputValue,
            value: inputValue
        };
        
        queryClient.setQueryData(['workOrderCategories'], (oldCategories = []) => {
            if (oldCategories.includes(inputValue)) {
                return oldCategories;
            }
            return [...oldCategories, inputValue];
        });

        if (isMulti) {
            const currentValues = value || [];
            const newValues = [...currentValues, inputValue];
            if (onChange) {
                onChange(newValues, [...(getSelectedValue() || []), newOption]);
            }
        } else {
            if (onChange) {
                onChange(inputValue, newOption);
            }
        }
    };


    return (
        <CustomReactSelect
            label={label}
            isMulti={isMulti}
            isClearable
            cacheOptions={false}
            loadOptions={handleFetch}
            value={getSelectedValue()}
            options={workOrderCategories}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            noOptionsMessage={() => "No work order categories found"}
            creatable={creatable}
            onCreateOption={creatable ? handleCreate : undefined}
            {...props}
        />
    );
};

WorkOrderCategorySelector.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
    ]),
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    isMulti: PropTypes.bool,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    creatable: PropTypes.bool,
};

export default WorkOrderCategorySelector;